import { useState, useMemo, useEffect } from 'react';
import AuthGuard from '@/components/feature/AuthGuard';
import AppLayout from '@/components/feature/AppLayout';
import { useTheme } from '@/hooks/useTheme';
import HebrewDateBanner from '@/components/feature/HebrewDateBanner';
import {
  fetchCallLogs, callStatusOptions, callDirectionOptions,
  type CallRecord, type DtmfLogEntry, type CallFilterOption, type CallSummary,
} from '@/services/calls';

const statusConfig: Record<string, { icon: string; color: string; bg: string; label: string }> = {
  answered: { icon: 'ri-check-line', color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Answered' },
  voicemail: { icon: 'ri-mic-line', color: 'text-amber-600', bg: 'bg-amber-50', label: 'Voicemail' },
  no_answer: { icon: 'ri-phone-find-line', color: 'text-orange-600', bg: 'bg-orange-50', label: 'No Answer' },
  busy: { icon: 'ri-close-line', color: 'text-red-500', bg: 'bg-red-50', label: 'Busy' },
  failed: { icon: 'ri-error-warning-line', color: 'text-red-600', bg: 'bg-red-50', label: 'Failed' },
  ringing: { icon: 'ri-phone-line', color: 'text-sky-600', bg: 'bg-sky-50', label: 'Ringing' },
  queued: { icon: 'ri-time-line', color: 'text-foreground-500', bg: 'bg-background-200', label: 'Queued' },
};

function formatTime(iso: string | null): string {
  if (!iso) return '--:--';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Jerusalem' });
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '--';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'Asia/Jerusalem' });
}

export function CallLogContent() {
  const { theme, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [habitFilter, setHabitFilter] = useState('all');
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const [callLogs, setCallLogs] = useState<CallRecord[]>([]);
  const [dtmfLogs, setDtmfLogs] = useState<DtmfLogEntry[]>([]);
  const [callHabitOptions, setCallHabitOptions] = useState<CallFilterOption[]>([{ label: 'All Habits', value: 'all' }]);
  const [callSummary, setCallSummary] = useState<CallSummary>({
    totalCalls: 0, todayCalls: 0, answeredRate: 0, avgDuration: 0,
    voicemailRate: 0, missedRate: 0, totalDtmfDigits: 0,
  });

  useEffect(() => {
    let mounted = true;
    fetchCallLogs().then((result) => {
      if (!mounted) return;
      setCallLogs(result.calls);
      setDtmfLogs(result.dtmfLogs);
      setCallHabitOptions(result.habitOptions);
      setCallSummary(result.summary);
    });
    return () => { mounted = false; };
  }, []);

  const filteredLogs = useMemo(() => {
    return callLogs.filter((call) => {
      if (statusFilter !== 'all' && call.status !== statusFilter) return false;
      if (directionFilter !== 'all' && call.direction !== directionFilter) return false;
      if (habitFilter !== 'all' && call.habitTypeId !== habitFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          call.phoneNumber.includes(q) ||
          call.habitName.toLowerCase().includes(q) ||
          call.status.toLowerCase().includes(q) ||
          call.userName.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [callLogs, statusFilter, directionFilter, habitFilter, searchQuery]);

  const selectedCall = callLogs.find((c) => c.id === expandedCallId) || null;
  const selectedDtmfLogs = expandedCallId ? dtmfLogs.filter((d) => d.callId === expandedCallId) : [];

  const toggleExpand = (callId: string) => {
    setExpandedCallId(expandedCallId === callId ? null : callId);
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
      <header className="h-16 flex items-center justify-between px-4 md:px-6 border-b border-background-300/40 bg-cream/60 dark:bg-midnight-deep/60 backdrop-blur flex-shrink-0">
        <h2 className="font-display text-xl font-bold text-foreground-950">Call Log</h2>
        <div className="flex items-center gap-1">
          <button className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:text-gold hover:bg-gold/10 transition cursor-pointer" title="Notifications">
            <i className="ri-notification-3-line text-lg" />
          </button>
          <button
            onClick={toggleTheme}
            className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:text-gold hover:bg-gold/10 transition cursor-pointer"
            title="Toggle dark mode"
          >
            <i className={theme === 'dark' ? 'ri-sun-line text-lg' : 'ri-moon-line text-lg'} />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {/* Hebrew Date Banner */}
        <div className="mb-4">
          <HebrewDateBanner />
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 mb-5">
          <div className="card p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded-md bg-primary-50 flex items-center justify-center">
                <i className="ri-phone-fill text-primary-500 text-xs" />
              </span>
            </div>
            <p className="text-lg font-heading font-bold text-foreground-950">{callSummary.totalCalls}</p>
            <p className="text-[10px] text-foreground-500">Total Calls</p>
          </div>
          <div className="card p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded-md bg-emerald-50 flex items-center justify-center">
                <i className="ri-phone-line text-emerald-500 text-xs" />
              </span>
            </div>
            <p className="text-lg font-heading font-bold text-foreground-950">{callSummary.todayCalls}</p>
            <p className="text-[10px] text-foreground-500">Today</p>
          </div>
          <div className="card p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded-md bg-emerald-50 flex items-center justify-center">
                <i className="ri-check-double-line text-emerald-500 text-xs" />
              </span>
            </div>
            <p className="text-lg font-heading font-bold text-emerald-600">{callSummary.answeredRate}%</p>
            <p className="text-[10px] text-foreground-500">Answer Rate</p>
          </div>
          <div className="card p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded-md bg-amber-50 flex items-center justify-center">
                <i className="ri-timer-line text-amber-500 text-xs" />
              </span>
            </div>
            <p className="text-lg font-heading font-bold text-foreground-950">{callSummary.avgDuration}s</p>
            <p className="text-[10px] text-foreground-500">Avg Duration</p>
          </div>
          <div className="card p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded-md bg-amber-50 flex items-center justify-center">
                <i className="ri-mic-line text-amber-500 text-xs" />
              </span>
            </div>
            <p className="text-lg font-heading font-bold text-foreground-950">{callSummary.voicemailRate}%</p>
            <p className="text-[10px] text-foreground-500">Voicemail</p>
          </div>
          <div className="card p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded-md bg-red-50 flex items-center justify-center">
                <i className="ri-close-line text-red-500 text-xs" />
              </span>
            </div>
            <p className="text-lg font-heading font-bold text-foreground-950">{callSummary.missedRate}%</p>
            <p className="text-[10px] text-foreground-500">Missed</p>
          </div>
          <div className="card p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded-md bg-accent-50 flex items-center justify-center">
                <i className="ri-number-2 text-accent-500 text-xs" />
              </span>
            </div>
            <p className="text-lg font-heading font-bold text-foreground-950">{callSummary.totalDtmfDigits}</p>
            <p className="text-[10px] text-foreground-500">DTMF Digits</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-[320px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              type="text"
              placeholder="Search calls..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 cursor-pointer whitespace-nowrap"
          >
            {callStatusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select
            value={directionFilter}
            onChange={(e) => setDirectionFilter(e.target.value)}
            className="px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 cursor-pointer whitespace-nowrap"
          >
            {callDirectionOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select
            value={habitFilter}
            onChange={(e) => setHabitFilter(e.target.value)}
            className="px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 cursor-pointer whitespace-nowrap"
          >
            {callHabitOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <span className="text-xs text-foreground-400 ml-auto whitespace-nowrap">
            {filteredLogs.length} of {callLogs.length} calls
          </span>
        </div>

        {/* Table */}
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/60 bg-background-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Habit</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Direction</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Scheduled</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Duration</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">DTMF</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Retries</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Delta</th>
                  <th className="w-10 px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((call) => {
                  const status = statusConfig[call.status] || statusConfig.queued;
                  const isExpanded = expandedCallId === call.id;
                  const callDtmfLogs = dtmfLogs.filter((d) => d.callId === call.id);

                  return (
                    <tr key={call.id} className="table-row border-b border-background-200/60 hover:bg-background-50 transition">
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">{formatDateShort(call.scheduledAt)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 flex items-center justify-center">
                            <i className={`${call.habitIcon} text-foreground-500 text-sm`} />
                          </span>
                          <span className="text-sm text-foreground-800 whitespace-nowrap">{call.habitName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap ${
                          call.direction === 'outbound' ? 'bg-sky-100 text-sky-700' : 'bg-purple-100 text-purple-700'
                        }`}>
                          {call.direction === 'outbound' ? (
                            <i className="ri-arrow-right-up-line" />
                          ) : (
                            <i className="ri-arrow-left-down-line" />
                          )}
                          {call.direction}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.color} whitespace-nowrap`}>
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className={`${status.icon} text-[10px]`} />
                          </span>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-700 font-mono whitespace-nowrap">{formatTime(call.scheduledAt)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-700 font-mono whitespace-nowrap">{formatDuration(call.durationSeconds)}</span>
                      </td>
                      <td className="px-4 py-3">
                        {call.dtmfInput ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent-50 text-accent-700 font-mono whitespace-nowrap">
                            {call.dtmfInput}
                            {call.dtmfInput !== call.dtmfExpected && (
                              <span className="text-red-500 ml-0.5" title={`Expected: ${call.dtmfExpected}`}>!</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-xs text-foreground-300 whitespace-nowrap">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {call.retryCount > 0 ? (
                          <span className={`text-xs font-medium whitespace-nowrap ${
                            call.retryCount >= 3 ? 'text-red-500' : 'text-amber-600'
                          }`}>
                            {call.retryCount}
                          </span>
                        ) : (
                          <span className="text-xs text-foreground-300 whitespace-nowrap">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {call.deltaMinutes !== null ? (
                          <span className={`text-xs font-medium font-mono whitespace-nowrap ${
                            call.deltaMinutes < 0 ? 'text-emerald-600' : 'text-amber-600'
                          }`}>
                            {call.deltaMinutes > 0 ? '+' : ''}{call.deltaMinutes} min
                          </span>
                        ) : (
                          <span className="text-xs text-foreground-300 whitespace-nowrap">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => toggleExpand(call.id)}
                          className={`w-7 h-7 flex items-center justify-center rounded-md transition cursor-pointer whitespace-nowrap ${
                            isExpanded
                              ? 'bg-primary-50 text-primary-600'
                              : 'text-foreground-400 hover:text-foreground-600 hover:bg-background-200'
                          }`}
                        >
                          <i className={`text-sm ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {filteredLogs.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center text-sm text-foreground-400">
                      No calls match the current filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Expanded DTMF detail row */}
          {expandedCallId && selectedCall && (
            <div className="border-t-2 border-primary-200/60 bg-background-50/60">
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-heading font-semibold text-foreground-950">
                    Call Detail — {selectedCall.habitName} on {formatDateShort(selectedCall.scheduledAt)}
                  </h4>
                  <button
                    onClick={() => setExpandedCallId(null)}
                    className="w-6 h-6 flex items-center justify-center rounded text-foreground-400 hover:text-foreground-600 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-background-100 rounded-md p-3">
                    <p className="text-[10px] text-foreground-400 uppercase font-semibold">Phone Number</p>
                    <p className="text-sm font-mono text-foreground-800 mt-0.5">{selectedCall.phoneNumber}</p>
                  </div>
                  <div className="bg-background-100 rounded-md p-3">
                    <p className="text-[10px] text-foreground-400 uppercase font-semibold">Started At</p>
                    <p className="text-sm font-mono text-foreground-800 mt-0.5">{formatTime(selectedCall.startedAt)}</p>
                  </div>
                  <div className="bg-background-100 rounded-md p-3">
                    <p className="text-[10px] text-foreground-400 uppercase font-semibold">Ended At</p>
                    <p className="text-sm font-mono text-foreground-800 mt-0.5">{formatTime(selectedCall.endedAt)}</p>
                  </div>
                  <div className="bg-background-100 rounded-md p-3">
                    <p className="text-[10px] text-foreground-400 uppercase font-semibold">Recording</p>
                    <p className="text-sm text-foreground-800 mt-0.5">
                      {selectedCall.recordingUrl ? (
                        <span className="text-primary-600 cursor-pointer hover:underline whitespace-nowrap">Play Recording</span>
                      ) : (
                        <span className="text-foreground-300">Not available</span>
                      )}
                    </p>
                  </div>
                </div>

                {/* DTMF Log */}
                <div>
                  <h5 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                    DTMF Input Log {selectedDtmfLogs.length > 0 && `(${selectedDtmfLogs.length})`}
                  </h5>
                  {selectedDtmfLogs.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-background-200/60">
                            <th className="text-left px-3 py-2 text-[10px] font-semibold text-foreground-400 uppercase whitespace-nowrap">#</th>
                            <th className="text-left px-3 py-2 text-[10px] font-semibold text-foreground-400 uppercase whitespace-nowrap">Digit</th>
                            <th className="text-left px-3 py-2 text-[10px] font-semibold text-foreground-400 uppercase whitespace-nowrap">Timestamp</th>
                            <th className="text-left px-3 py-2 text-[10px] font-semibold text-foreground-400 uppercase whitespace-nowrap">Menu Path</th>
                            <th className="text-left px-3 py-2 text-[10px] font-semibold text-foreground-400 uppercase whitespace-nowrap">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedDtmfLogs.map((entry, idx) => (
                            <tr key={entry.id} className="border-b border-background-200/40 last:border-0">
                              <td className="px-3 py-2 text-foreground-500 font-mono">{idx + 1}</td>
                              <td className="px-3 py-2">
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent-100 text-accent-700 font-mono font-bold text-sm">
                                  {entry.digit}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-foreground-600 font-mono whitespace-nowrap">{formatTime(entry.pressedAt)}</td>
                              <td className="px-3 py-2 text-foreground-600 font-mono text-[11px] whitespace-nowrap">{entry.menuPath}</td>
                              <td className="px-3 py-2">
                                <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent-50 text-accent-700 whitespace-nowrap">
                                  {entry.action}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="bg-background-100 rounded-md p-6 text-center">
                      <p className="text-xs text-foreground-400">No DTMF input detected for this call</p>
                      <p className="text-[10px] text-foreground-300 mt-1">
                        Expected digit: <span className="font-mono text-accent-600">{selectedCall.dtmfExpected}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CallLogPage() {
  return (
    <AuthGuard>
      <AppLayout>
        <CallLogContent />
      </AppLayout>
    </AuthGuard>
  );
}